import React from 'react';
import { FormControlLabel, Switch } from '@mui/material';
// Components
import MuiDataGrid from '../muidatagrid';
import { createKpiColumns } from '../muidatagrid/columnDefs/kpiColumns';
// Languages
import lang from '../../languages/en.json';
// Models
import { KpiGrid } from '../../models/operationsmodels/KpiModels';
// Redux
import { useAppSelector } from '../../redux';
import { selectIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';
// Utils
import { formatLoadDate } from '../../utils/appUtils';
import type { ClickHandler } from '../../utils/usageMetricsLogger';
// Constants
import { CARD_TITLES } from '../../constants/constants';
// Styles
import styles from './KpiGridSection.module.css';
import pagesStandardStyles from '../../pages/Pages.module.css';
/**
 * Props interface for KpiGridSection component
 */
interface KpiGridSectionProps {
  /** Section data containing title, loadDate, and kpiDetails */
  section: KpiGrid;
  /** Whether peer group data is visible */
  isPeerGroupVisible: boolean;
  /** Handler for peer group toggle */
  onTogglePeerGroup: (isVisible: boolean) => void;
  /** Optional action button(s) to display in header controls - can be single button or multiple */
  actionButtons?: React.ReactNode;
  /** Optional click handler for usage metrics logging */
  logClick?: ClickHandler;
  /** Optional handler to open the disclaimer dialog (threaded to grid cell renderers) */
  onDisclaimerOpen?: () => void;
}
/**
 * KpiGridSection Component - Reusable KPI grid section with header and controls
 * Features:
 * - Section title and load date
 * - Optional action button (e.g., Marketing Planning Tool, Top Selling SKUs)
 * - Peer group data toggle
 * - Data grid with KPI details
 */
const KpiGridSection: React.FC<KpiGridSectionProps> = ({
  section,
  isPeerGroupVisible,
  onTogglePeerGroup,
  actionButtons,
  logClick,
  onDisclaimerOpen,
}) => {

  // Access global state to determine if multi-store features are enabled
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  return (
    <div className={styles.gridSections}>
      {/* Section Header */}
      <div className={`${pagesStandardStyles.headerContainer} ${styles.headerContainer}`}>
        <div className={styles.headerInfo}>
          <h2 className={pagesStandardStyles.pageTitle}>{section.title}</h2>
          <p className={pagesStandardStyles.pageSubtitle}>
            {lang.pageContent.datePrefix} {section?.loadDate ? formatLoadDate(section.loadDate, false, -1) : 'N/A'}
          </p>
        </div>
        {/* Header Controls */}
        <div className={styles.headerControls}>
          {/* Optional action button(s) */}
          {actionButtons}
          {/* Peer group toggle */}
          <div className={styles.toggleContainer}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPeerGroupVisible}
                  onChange={e => onTogglePeerGroup(e.target.checked)}
                  color="primary"
                />
              }
              label={lang.pageContent.toggleShowPeerGroup}
            />
          </div>
        </div>
      </div>
      {/* Data Grid */}
      <MuiDataGrid
        gridKey={`datagrid-${isPeerGroupVisible ? 'with-peer' : 'without-peer'}`}
        rows={section.kpiDetails}
        getRowId={row => row.kpiCode}
        createColumns={createKpiColumns}
        columnArgs={[isPeerGroupVisible, isMultiStoreEnabled, section.title, section.cardName]} // Pass necessary args for column creation
        logClick={logClick}
        onDisclaimerOpen={onDisclaimerOpen}
        // B2B only: value columns auto-grow to fit their values (KPI column stays fixed),
        // pulling width from utility columns. Truncation + tooltip remains the fallback.
        autoFitValueColumns={section.cardName === CARD_TITLES.B2B}
      />
    </div>
  );
};
export default KpiGridSection;
